import {
  ChangeDetectionStrategy, Component, DOCUMENT, ElementRef, afterNextRender, inject, input, viewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { IconComponent } from '@kin/spark-ui';
import { SignupResetService } from '../signup-reset.service';

/**
 * - `problem`: something failed; trying again may work.
 * - `wait`: the backend is throttling this visitor; nothing to do but wait.
 */
export type SignupErrorTone = 'problem' | 'wait';

/**
 * The sign-up pages' error state: a badge, the message as the page heading and, with `back`, a Back button
 * that returns to the previous page (the address-first landing page, for /signup) or, with `restart`, a Start over
 * button that clears the visit and goes to the landing page.
 *
 * Copy is set by the hosts; run it through kin-brand-messaging before this ships.
 */
@Component({
  selector: 'app-signup-error',
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

    /* Same measure and entrance as /enrolled, so the flow's end states read as one family */
    :host {
      display: block; max-width: 560px; margin: 0 auto; padding: 72px 24px 96px; text-align: center;
      animation: fadeUp 400ms var(--kin-ease-out);
    }

    /* Red-600 on Red-100 (6.5:1) for a failure; Neutral-1000 on Yellow-400 (14.8:1) for a wait, which isn't one */
    .badge {
      display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px;
      border-radius: var(--kin-radius-pill); margin-bottom: 24px;
    }
    .badge.problem { background: var(--kin-red-100); color: var(--kin-red-600); }
    .badge.wait { background: var(--kin-yellow-400); color: var(--kin-neutral-1000); }

    /* Heading 2: the message is a sentence, not a headline */
    h1 {
      font-size: 28px; font-weight: 600; line-height: 1.3; letter-spacing: -0.5px; margin: 0;
      text-wrap: balance;
    }
    h1:focus { outline: none; }

    /* Solid Yellow-600 CTA with Neutral-1000 text (12.2:1), as on /enrolled; the arrow moves only on hover */
    .cta {
      display: inline-flex; align-items: center; justify-content: center; gap: 10px;
      min-height: 52px; min-width: 160px; padding: 14px 28px; margin-top: 32px; box-sizing: border-box;
      font: inherit; font-size: 16px; font-weight: 600; line-height: 1.25; letter-spacing: -0.1px;
      color: var(--kin-neutral-1000); background: var(--kin-yellow-600);
      border: none; border-radius: var(--kin-radius-pill); cursor: pointer;
      transition: box-shadow 120ms var(--kin-ease-out);
    }
    .cta-icon { flex-shrink: 0; transition: transform 160ms var(--kin-ease-out); }
    .cta:hover, .cta:active { box-shadow: var(--kin-shadow-focus); }
    .cta:hover .cta-icon { transform: translateX(-3px); }
    .cta:focus-visible { outline: none; box-shadow: var(--kin-shadow-focus); }

    /* Body Small in Neutral-600 (4.6:1 on Yellow-100) */
    .questions { margin: 40px 0 0; font-size: 14px; line-height: 1.5; color: var(--kin-text-tertiary); }
    .questions a { color: var(--kin-text-secondary); font-weight: 600; }

    @media (max-width: 600px) {
      :host { padding: 40px 16px 56px; }
      h1 { font-size: 22px; letter-spacing: 0; }
      .cta { width: 100%; }
    }
    @media (prefers-reduced-motion: reduce) {
      :host { animation: none; }
      .cta, .cta-icon { transition: none; }
      .cta:hover .cta-icon { transform: none; }
    }
  `,
  template: `
    <div class="badge" [class]="tone()" aria-hidden="true">
      <ngk-icon [icon]="tone() === 'wait' ? 'clock' : 'alert-triangle'" size="28px" />
    </div>
    <!-- Focused on arrival (see constructor), so a screen reader starts at the message -->
    <h1 #heading tabindex="-1">{{ message() }}</h1>

    @if (back()) {
      <button type="button" class="cta" (click)="goBack()">
        <ngk-icon class="cta-icon" icon="arrow-left" size="18px" />Back
      </button>
    }
    @if (restart()) {
      <button type="button" class="cta" (click)="startOver()">
        <ngk-icon class="cta-icon" icon="rotate-ccw" size="18px" />Start over
      </button>
    }

    <p class="questions">Questions? Reach us at <a [href]="'tel:' + phoneNumber">{{ phoneNumber }}</a>.</p>
  `,
})
export class SignupErrorComponent {
  private location = inject(Location);
  private document = inject(DOCUMENT);
  private reset = inject(SignupResetService);

  readonly message = input.required<string>();
  readonly tone = input<SignupErrorTone>('problem');
  /** Shows the Back button. */
  readonly back = input(false);
  /** Shows the Start over button. */
  readonly restart = input(false);

  // Property tax support line
  readonly phoneNumber = '214-216-6542';

  private readonly heading = viewChild.required<ElementRef<HTMLHeadingElement>>('heading');

  constructor() {
    afterNextRender(() => this.heading().nativeElement.focus({ preventScroll: true }));
  }

  /** History back; opened in a fresh tab (nothing to go back to), the landing page instead. */
  protected goBack(): void {
    if ((this.document.defaultView?.history.length ?? 0) > 1) this.location.back();
    else this.reset.exitToLanding();
  }

  protected startOver(): void {
    this.reset.exitToLanding();
  }
}
